import calcCharacteristicValues from './calcCharacteristicValues'
import getFatigueDetails, { getFatigueDetailsForLevel } from './getFatigueDetails'
import syncCharacterFatigueAdjustments from './syncCharacterFatigueAdjustments'

function syncTopLevelCharacteristicFromEntry(character, characteristicName) {
  if (!Array.isArray(character?.characteristics)) {
    return
  }

  const characteristic = character.characteristics.find(
    (entry) => entry?.name === characteristicName,
  )

  if (!characteristic) {
    return
  }

  const prefix = `${characteristicName}_`

  if (character[`${prefix}rolled`] == null || character[`${prefix}rolled`] === false) {
    character[`${prefix}rolled`] = characteristic.rolled ?? 0
  }

  if (character[`${prefix}improvs_tot`] == null || character[`${prefix}improvs_tot`] === false) {
    character[`${prefix}improvs_tot`] = characteristic.improvs_tot ?? 0
  }

  if (character[`${prefix}adjs_tot`] == null || character[`${prefix}adjs_tot`] === false) {
    character[`${prefix}adjs_tot`] = characteristic.adjs_tot ?? 0
  }

  if (character[`${prefix}incr_percent`] == null || character[`${prefix}incr_percent`] === false) {
    character[`${prefix}incr_percent`] = characteristic.incr_percent ?? 0
  }
}

function getNegativeMagnitude(value) {
  const hpCur = Number(value) || 0
  return hpCur < 0 ? Math.abs(hpCur) : 0
}

function getNegativeHpWounds(armourLocations) {
  if (!Array.isArray(armourLocations)) {
    return 0
  }

  return armourLocations.reduce((total, location) => {
    return total + getNegativeMagnitude(location?.hp_cur)
  }, 0)
}

function getNextWounds(character, options = {}) {
  const currentWounds = Math.max(Number(character?.wounds) || 0, 0)

  if (Object.hasOwn(options, 'manualWounds')) {
    return Math.max(Number(options.manualWounds) || 0, 0)
  }

  const hpChange = options?.hpChange
  if (hpChange && typeof hpChange === 'object') {
    const previousNegative = getNegativeMagnitude(hpChange.previousHpCur)
    const nextNegative = getNegativeMagnitude(hpChange.nextHpCur)
    const woundDelta = Math.max(nextNegative - previousNegative, 0)
    return currentWounds + woundDelta
  }

  return currentWounds
}

function getOverrideFatigueLevel(character, options = {}) {
  if (Object.hasOwn(options, 'overrideFatigue')) {
    return options.overrideFatigue || null
  }

  const overrideFatigue = character?.fatigue_override
  return overrideFatigue ? overrideFatigue : null
}

function resetArmourLocationsForReactivation(armourLocations) {
  if (!Array.isArray(armourLocations)) {
    return
  }

  armourLocations.forEach((location) => {
    location.hp_cur = Number(location?.hp_tot) || 0
  })
}

export default function syncCharacterCombatStateFromArmourLocations(character, options = {}) {
  if (!character || typeof character !== 'object') {
    return character
  }

  if (options?.reactivate === true) {
    resetArmourLocationsForReactivation(character.armour_locations)
    options = {
      ...options,
      manualWounds: 0,
      overrideFatigue: null,
    }
  }

  const nextWounds = getNextWounds(character, options)
  const armourLocationWounds = getNegativeHpWounds(character.armour_locations)
  const overrideFatigue = getOverrideFatigueLevel(character, options)

  syncTopLevelCharacteristicFromEntry(character, 'con')

  character.wounds = nextWounds
  character.con_wounds_tot = nextWounds
  character.fatigue_override = overrideFatigue
  calcCharacteristicValues({ pfx: 'con', character })

  if (Array.isArray(character.characteristics)) {
    const conCharacteristic = character.characteristics.find((entry) => entry?.name === 'con')

    if (conCharacteristic) {
      conCharacteristic.cur = character.con_cur
    }
  }

  const fatigueDetails = getFatigueDetails({
    con_cur: Number(character.con_cur) || 0,
    con_wounds: armourLocationWounds > nextWounds ? armourLocationWounds : nextWounds,
  })
  const effectiveFatigueDetails = overrideFatigue
    ? getFatigueDetailsForLevel(overrideFatigue)
    : fatigueDetails

  if (Number(character.con_cur) <= 0) {
    character.status = 'Dead'
    character.fatigue = overrideFatigue || 'Debilitated'
  } else {
    character.status = 'Alive'
    character.fatigue = effectiveFatigueDetails.level || 'Fresh'
  }

  character = syncCharacterFatigueAdjustments(character, effectiveFatigueDetails)

  return character
}
